import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ConversaGestorStore, TrechoMensagemGestor, trechosDaMensagem} from './conversa-gestor.store';
import {EstadoCanalSimuladoMiaGestor, EstadoChecklistMiaGestor, MensagemCanalSimuladoMiaGestor, MiaGestorService} from './mia-gestor.service';

interface PassoDaFaixa {
  chave: string;
  rotulo: string;
  estado: 'pendente' | 'andamento' | 'concluido' | 'atencao';
  detalhe: string;
}

const DETALHE_DO_PASSO: {[estado: string]: string} = {
  PENDENTE: 'aguardando', EM_ANDAMENTO: 'em andamento', PARCIAL: 'parcial', CONCLUIDO: 'concluído',
  DNS_PENDENTE: 'publicação pendente', DISPONIVEL: 'disponível', ERRO: 'requer atenção',
};

/**
 * Simulador que É o canal: o operador conversa como um contato desconhecido
 * entrando pelo mesmo despachante do WhatsApp — saudação, conversa comercial
 * (SDR), criação do teste e implantação, sem atalho.
 *
 * A tela não deriva bolha nenhuma do estado: tudo o que aparece foi "enviado"
 * pela Mia pelo transporte simulado, com os cortes da Cloud já aplicados. Tocar
 * num botão manda o id do reply button, não o rótulo — igual ao webhook.
 */
@Component({
  selector: 'app-canal-simulado-mia-gestor',
  templateUrl: './canal-simulado-mia-gestor.component.html',
  styleUrls: ['./painel-mia-gestor.component.scss'],
  // só o que o painel não tem: a trava destravada e o item de lista clicável
  styles: [`
    .mensagem__copiar {
      border: 0; background: transparent; color: #667781;
      padding: 6px 10px; cursor: pointer; font-size: 12px;
    }
    .mensagem__copiar:hover { color: #008069; }
    .mensagem__copiar:disabled { opacity: .45; cursor: not-allowed; }
    .balao > .mensagem__copiar,
    .nota > .mensagem__copiar,
    .janela__topo > .conversa__copiar { opacity: 0; pointer-events: none; }
    .balao:hover > .mensagem__copiar,
    .balao:focus-within > .mensagem__copiar,
    .nota:hover > .mensagem__copiar,
    .nota:focus-within > .mensagem__copiar,
    .janela__topo:hover > .conversa__copiar,
    .janela__topo:focus-within > .conversa__copiar { opacity: 1; pointer-events: auto; }
    .balao:hover > .mensagem__copiar:disabled,
    .balao:focus-within > .mensagem__copiar:disabled,
    .nota:hover > .mensagem__copiar:disabled,
    .nota:focus-within > .mensagem__copiar:disabled,
    .janela__topo:hover > .conversa__copiar:disabled,
    .janela__topo:focus-within > .conversa__copiar:disabled { opacity: .45; }
    .opcao__linha {
      display: flex; width: calc(100% + 22px); margin: 6px -11px -5px;
      border-top: 1px solid rgba(17, 27, 33, .08);
    }
    .opcao__linha + .opcao__linha { margin-top: 0; }
    .opcao__linha .balao__botao { flex: 1; min-width: 0; width: auto; margin: 0; border-top: 0; }
    .opcao__linha .mensagem__copiar { flex: none; opacity: 0; pointer-events: none; }
    .opcao__linha:hover .mensagem__copiar,
    .opcao__linha:focus-within .mensagem__copiar { opacity: 1; pointer-events: auto; }
    .opcao__linha:hover .mensagem__copiar:disabled,
    .opcao__linha:focus-within .mensagem__copiar:disabled { opacity: .45; }
    .gestor__simulador--real { color: #b45309; border-color: #f59e0b; }
    .folha__itens li[role="button"] { cursor: pointer; }
    .folha__itens li[role="button"]:hover { background: rgba(0, 0, 0, 0.04); }
  `],
  providers: [ConversaGestorStore],
})
export class CanalSimuladoMiaGestorComponent implements OnInit {
  @ViewChild('corpoChat') corpoChat: ElementRef;
  @ViewChild('campoMensagem') campoMensagem: ElementRef;

  texto = '';
  /** trava: a confirmação do teste e as aprovações não executam — o resto da jornada segue */
  naoExecutarAcoes = true;
  confirmandoReinicio = false;
  estado: EstadoCanalSimuladoMiaGestor | null = null;
  carregando = true;
  enviando = false;
  reiniciando = false;
  erro = '';
  copiando = false;
  avisoCopia = '';
  /** list message aberta como folha, igual ao celular */
  folha: MensagemCanalSimuladoMiaGestor['lista'] | null = null;
  /** eco do que foi enviado, até o canal devolver o estado com a mensagem já gravada */
  private pendentes: MensagemCanalSimuladoMiaGestor[] = [];
  private proximoIdDoEco = -1;

  sugestoes = ['oi', 'quanto custa?', 'como funciona?', 'quero testar'];

  /** a lateral (app-estado-gestor) lê a store; aqui ela só recebe o que o canal devolveu */
  constructor(public conversa: ConversaGestorStore, private servico: MiaGestorService) {}

  ngOnInit(): void {
    this.conversa.verifiqueAgente();
    this.carregando = true;
    this.servico.obtenhaCanalSimulado()
      .then(estado => this.aplique(estado))
      .catch(erro => { this.erro = this.textoDoErro(erro, 'Não foi possível abrir o canal simulado.'); })
      .then(() => { this.carregando = false; this.roleAteOFim(); });
  }

  /**
   * O que está na tela: o que veio do canal mais o eco do que acabou de ser
   * enviado. Sem o eco a bolha do operador só apareceria depois do turno
   * inteiro (LLM incluso) — no WhatsApp ela aparece na hora do Enter.
   * Sem pendente devolve a MESMA referência, para não mexer na lista à toa.
   */
  get mensagens(): MensagemCanalSimuladoMiaGestor[] {
    const enviadas = this.estado ? this.estado.mensagens : [];
    return this.pendentes.length ? enviadas.concat(this.pendentes) : enviadas;
  }

  get fase(): string {
    return this.estado ? this.estado.fase : 'INICIO';
  }

  async copieConversa(): Promise<void> {
    if (!this.mensagens.length || this.copiando) return;
    const texto = this.mensagens.map(item => this.textoParaCopia(item, true)).join('\n\n');
    await this.copieTexto(texto, 'Conversa copiada!');
  }

  async copieMensagem(item: MensagemCanalSimuladoMiaGestor): Promise<void> {
    if (this.copiando) return;
    await this.copieTexto(this.textoParaCopia(item, false), 'Mensagem copiada!');
  }

  async copieOpcao(titulo: string): Promise<void> {
    if (this.copiando) return;
    await this.copieTexto(titulo, 'Opção copiada!');
  }

  private textoParaCopia(item: MensagemCanalSimuladoMiaGestor, incluaAutor: boolean): string {
    const autor = item.papel === 'usuario' ? 'Você' : item.papel === 'gestor' ? 'MIA Gestor' : 'Nota';
    const linhas = [incluaAutor ? `${autor}: ${item.texto}` : item.texto];
    if (item.botoes && item.botoes.length) {
      linhas.push(`Opções: ${item.botoes.map(botao => botao.titulo).join(' | ')}`);
    }
    if (item.link) linhas.push(`${item.link.rotulo}: ${item.link.url}`);
    if (item.lista) {
      linhas.push(`${item.lista.rotulo}:`);
      item.lista.itens.forEach(opcao => linhas.push(
        `- ${opcao.titulo}${opcao.descricao ? ': ' + opcao.descricao : ''}`,
      ));
    }
    return linhas.join('\n');
  }

  private async copieTexto(texto: string, sucesso: string): Promise<void> {
    this.copiando = true;
    this.avisoCopia = '';
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(texto);
      } else {
        const campo = document.createElement('textarea');
        const focoAnterior = document.activeElement as HTMLElement;
        campo.value = texto;
        campo.style.position = 'fixed';
        campo.style.opacity = '0';
        document.body.appendChild(campo);
        try {
          campo.select();
          if (!document.execCommand('copy')) throw new Error('Cópia indisponível');
        } finally {
          campo.remove();
          if (focoAnterior) focoAnterior.focus();
        }
      }
      this.avisoCopia = sucesso;
    } catch (_) {
      this.avisoCopia = 'Não foi possível copiar. Tente novamente.';
    } finally {
      this.copiando = false;
    }
  }

  /** o getter monta objetos novos a cada ciclo; sem trackBy o ngFor recriava os <li> e a animação de entrada repetia */
  chaveDoPasso(_indice: number, passo: PassoDaFaixa): string {
    return passo.chave;
  }

  /** faixa de progresso: antes do teste, o primeiro passo mostra a fase comercial */
  get passos(): PassoDaFaixa[] {
    const lista = this.conversa.checklist;
    const comercial = !this.estado || !this.estado.onboarding;
    return [
      comercial
        ? {chave: 'empresa', rotulo: 'Empresa', estado: this.fase === 'INICIO' ? 'pendente' : 'andamento', detalhe: this.rotuloDaFase(this.fase)}
        : this.passo('empresa', 'Empresa', lista.empresa, this.conversa.empresaId ? `#${this.conversa.empresaId}` : ''),
      this.passo('cardapio', 'Cardápio', lista.cardapio, ''),
      this.passo('entrega', 'Entrega', lista.formasEntrega, ''),
      this.passo('link', 'Link', lista.link, ''),
    ];
  }

  private passo(chave: string, rotulo: string, checklist: EstadoChecklistMiaGestor, complemento: string): PassoDaFaixa {
    let estado: PassoDaFaixa['estado'] = 'pendente';
    if (checklist === 'CONCLUIDO' || checklist === 'DISPONIVEL') estado = 'concluido';
    else if (checklist === 'ERRO' || checklist === 'PARCIAL') estado = 'atencao';
    else if (checklist !== 'PENDENTE') estado = 'andamento';
    const base = DETALHE_DO_PASSO[checklist] || String(checklist).toLowerCase();
    return {chave, rotulo, estado, detalhe: complemento ? `${base} · ${complemento}` : base};
  }

  abraFolha(lista: MensagemCanalSimuladoMiaGestor['lista']): void {
    this.folha = lista || null;
  }

  fecheFolha(): void {
    this.folha = null;
  }

  escolhaDaLista(item: {id?: string; titulo: string}): void {
    this.fecheFolha();
    this.toque(item);
  }

  rotuloDaFase(fase: string): string {
    return ({INICIO: 'início', SDR: 'conversa comercial', CRIAR_TESTE: 'criando o teste', AUTENTICADO: 'teste criado'} as any)[fase] || fase;
  }

  partesDaMensagem(texto: string): TrechoMensagemGestor[] {
    return trechosDaMensagem(texto);
  }

  rotuloDeBotao(texto: string): string {
    return String(texto || '').slice(0, 20);
  }

  rotuloFoiCortado(texto: string): boolean {
    return String(texto || '').length > 20;
  }

  aoTeclar(evento: KeyboardEvent): void {
    if (evento.key !== 'Enter' || evento.shiftKey || (evento as any).isComposing) return;
    evento.preventDefault();
    this.envie();
  }

  envie(): void {
    const mensagem = this.texto.trim();
    if (!mensagem || this.enviando) return;
    this.texto = '';
    this.despache({texto: mensagem});
  }

  envieSugestao(sugestao: string): void {
    this.texto = sugestao;
    this.envie();
  }

  /** reply button ou item de lista: vai o id, como o button_reply/list_reply da Cloud */
  toque(botao: {id?: string; titulo: string}): void {
    if (this.enviando) return;
    this.despache({botao: {id: botao.id || botao.titulo, titulo: botao.titulo}});
  }

  reinicie(): void {
    this.confirmandoReinicio = false;
    if (this.reiniciando) return;
    this.reiniciando = true;
    this.erro = '';
    this.servico.reinicieCanalSimulado()
      .then(estado => this.aplique(estado))
      .catch(erro => { this.erro = this.textoDoErro(erro, 'Não foi possível começar do zero.'); })
      .then(() => { this.reiniciando = false; this.roleAteOFim(); });
  }

  private despache(entrada: {texto?: string; botao?: {id: string; titulo: string}}): void {
    this.enviando = true;
    this.erro = '';
    this.ecoe(entrada.botao ? entrada.botao.titulo : entrada.texto);
    this.roleAteOFim();
    this.servico.envieNoCanalSimulado(entrada, this.naoExecutarAcoes)
      .then(estado => { this.conversa.agenteDisponivel = true; this.aplique(estado); })
      .catch(erro => { this.erro = this.textoDoErro(erro, 'O canal não respondeu.'); })
      .then(() => {
        this.enviando = false;
        this.roleAteOFim();
        setTimeout(() => this.campoMensagem && this.campoMensagem.nativeElement.focus(), 0);
      });
  }

  /** bolha provisória do operador; sai quando o estado autoritativo chega */
  private ecoe(texto?: string): void {
    const limpo = String(texto || '').trim();
    if (!limpo) return;
    this.pendentes.push({
      id: this.proximoIdDoEco--,
      papel: 'usuario',
      texto: limpo,
      criadoEm: new Date().toISOString(),
    });
  }

  private aplique(estado: EstadoCanalSimuladoMiaGestor): void {
    this.pendentes = [];
    this.estado = estado;
    const onboarding = estado.onboarding;
    // antes do teste, a lateral mostra a fase comercial no lugar da etapa
    this.conversa.etapa = onboarding ? onboarding.etapa : estado.fase;
    this.conversa.status = onboarding ? onboarding.status : 'ATIVO';
    this.conversa.versao = onboarding ? onboarding.versao : 0;
    this.conversa.checklist = onboarding ? onboarding.checklist
      : {empresa: 'PENDENTE', cardapio: 'PENDENTE', formasEntrega: 'PENDENTE', link: 'PENDENTE'};
    this.conversa.acaoPendente = onboarding ? (onboarding.acaoPendente || null) : null;
    this.conversa.onboardingId = onboarding ? (onboarding.id || null) : null;
    this.conversa.empresaId = onboarding ? onboarding.empresaId : null;
    this.conversa.modeloAtual = estado.modelo;
  }

  private textoDoErro(erro: any, padrao: string): string {
    return erro && erro.error && erro.error.erro ? erro.error.erro : padrao;
  }

  private roleAteOFim(): void {
    setTimeout(() => {
      if (!this.corpoChat) return;
      const corpo = this.corpoChat.nativeElement;
      corpo.scrollTop = corpo.scrollHeight;
    }, 0);
  }
}
